import { Box, Typography, List, ListItem, useTheme } from '@mui/material'
import ViewTitle from '../../../components/ViewTitle'
import { TitleTypography, Header, Subhead, ContentWrapper, ContentText, ReferenceLink } from './styles'

export default function CopyrightPolicy() {
  const theme = useTheme()

  return (
    <Box component="main" sx={{ maxWidth: theme.breakpoints.values.xl, width: '100%', margin: '0 auto' }}>
      <ViewTitle title="Copyright Policy" />
      <Header>
        <Typography
          variant="h1"
          id="mainTitle"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' },
            color: '#fff',
            textAlign: 'center'
          }}
        >
          Photobucket Copyright Policy
        </Typography>
      </Header>
      <ContentWrapper component="article">
        <TitleTypography>Rev. June 9, 2021</TitleTypography>

        <Subhead>Reporting Claims of Copyright Infringement</Subhead>

        <ContentText>
          Photobucket.com, Inc. takes claims of copyright infringement seriously. We will respond to notices of alleged copyright infringement that
          comply with applicable law. If you believe any materials accessible on or from www.photobucket.com (&quot;Website&quot;) infringe your
          copyright, you may request removal of those materials (or access to them) from the Website by submitting written notification to our
          copyright agent designated below. In accordance with the Online Copyright Infringement Liability Limitation Act of the Digital Millennium
          Copyright Act (17 U.S.C. § 512) (&quot;DMCA&quot;), the written notice (the &quot;DMCA Notice&quot;) must include substantially the
          following:
          <ul>
            <li>Your physical or electronic signature.</li>
            <li>
              Identification of the copyrighted work you believe to have been infringed or, if the claim involves multiple works on the Website, a
              representative list of such works.
            </li>
            <li>
              Identification of the material or link you believe to be infringing in a sufficiently precise manner to allow us to locate that
              material, including at a minimum, if applicable, the URL of the link shown on the Website where such material may be found.
            </li>
            <li>
              Adequate information by which we can contact you (including your name, postal address, telephone number, and, if available, email
              address).
            </li>
            <li>
              A statement that you have a good faith belief that use of the copyrighted material is not authorized by the copyright owner, its agent,
              or the law.
            </li>
            <li>A statement that the information in the written notice is accurate.</li>
            <li>A statement, under penalty of perjury, that you are authorized to act on behalf of the copyright owner.</li>
          </ul>
        </ContentText>
        <ContentText>
          Our designated copyright agent to receive DMCA Notices is:
          <List sx={{ fontFamily: '"proxima-nova", sans-serif', fontSize: { xs: '1rem', lg: '1.375rem' }, lineHeight: 1.5 }}>
            <ListItem>DMCA AGENT PHOTOBUCKET.COM, INC.</ListItem>
            <ListItem>621 KALAMATH STE 135</ListItem>
            <ListItem>DENVER, CO 80204</ListItem>
            <ListItem>E-MAIL: ABUSE@PHOTOBUCKET.COM</ListItem>
            <ListItem>PHONE: 303-226-6800</ListItem>
          </List>
        </ContentText>
        <ContentText>
          We may also be searched on the US Copyright Office&apos;s online directory of designated agents as Photobucket Corporation.
        </ContentText>
        <ContentText>
          If you fail to comply with all of the requirements of Section 512(c)(3) of the DMCA, your DMCA Notice may not be effective.
        </ContentText>
        <ContentText>
          Please be aware that if you knowingly materially misrepresent that material or activity on the Website is infringing your copyright, you may
          be held liable for damages (including costs and attorneys&apos; fees) under Section 512(f) of the DMCA.
        </ContentText>
        <Subhead>Counter-Notification Procedures</Subhead>
        <ContentText>
          If you believe that material you posted on the Website was removed or access to it was disabled by mistake or misidentification, you may
          file a counter-notification with us (a &quot;Counter-Notice&quot;) by submitting written notification to our copyright agent designated
          above. Pursuant to the DMCA, the Counter-Notice must include substantially the following:
          <ul>
            <li>Your physical or electronic signature.</li>
            <li>
              An identification of the material that has been removed or to which access has been disabled and the location at which the material
              appeared before it was removed or access disabled.
            </li>
            <li>
              Adequate information by which we can contact you (including your name, postal address, telephone number, and, if available, email
              address).
            </li>
            <li>
              A statement under penalty of perjury by you that you have a good faith belief that the material identified above was removed or disabled
              as a result of a mistake or misidentification of the material to be removed or disabled.
            </li>
            <li>
              A statement that you will consent to the jurisdiction of the Federal District Court for the judicial district in which your address is
              located (or if you reside outside the United States for any judicial district in which the Website may be found) and that you will
              accept service from the person (or an agent of that person) who provided the Website with the complaint at issue.
            </li>
          </ul>
        </ContentText>
        <ContentText>
          The DMCA allows us to restore the removed content if the party filing the original DMCA Notice does not file a court action against you
          within ten business days of receiving the copy of your Counter-Notice.
        </ContentText>
        <ContentText>
          Please be aware that if you knowingly materially misrepresent that material or activity on the Website was removed or disabled by mistake or
          misidentification, you may be held liable for damages (including costs and attorneys&apos; fees) under Section 512(f) of the DMCA.
        </ContentText>

        <Subhead>Repeat Infringers</Subhead>
        <ContentText>
          It is our policy in appropriate circumstances to disable and/or terminate the accounts of users who are repeat infringers.
        </ContentText>

        <Subhead>Subpoenas</Subhead>
        <ContentText>
          Photobucket may be required to disclose information to individuals asserting rights under the DMCA. You hereby authorize Photobucket to
          release information about you if required by law or subpoena, without notice to you.
        </ContentText>
        <ContentText>
          You agree that you, and not Photobucket, are responsible for any violations of any intellectual property rights in any content that you
          upload to the Website.
        </ContentText>

        <Subhead>Notification of Trademark Infringement </Subhead>
        <ContentText>
          If you believe that your trademark (the &quot;Infringed Mark&quot;) is being used by a user in a way that constitutes trademark
          infringement, please provide our copyright agent (specified above) with the following information:
          <ul>
            <li>Your physical or electronic signature, or a physical or electronic signature of a person authorized to act on your behalf;</li>
            <li>
              Information reasonably sufficient to permit Photobucket to contact you or your authorized agent, including a name, address, telephone
              number and an email address;
            </li>
            <li>
              Identification of the Infringed Mark(s) claimed to have been infringed, including (i) for registered Infringed Marks, a copy of each
              relevant federal trademark registration certificate or (ii) for common law Infringed Marks, evidence sufficient to establish the time
              period and geographic area in which the Infringed Mark has been used by you;
            </li>
            <li>Information reasonably sufficient to permit Photobucket to identify the use being challenged;</li>
            <li>
              A statement that you have not authorized the challenged use, and that you have a good-faith belief that the challenged use is not
              authorized by law; and
            </li>
            <li>
              A statement that the information in the notification is accurate and, under penalty of perjury, that you are the Infringed Mark owner,
              or are authorized to act on behalf of the Infringed Mark owner.
            </li>
          </ul>
        </ContentText>
        <ContentText>
          Upon receipt of notice as described above, Photobucket will confirm the existence of the Infringed Mark on the Website, notify the user who
          posted the Infringed Mark, and take whatever action, in its sole discretion, it deems appropriate, including removal of the Infringed Mark
          from the Website. A registered user may respond to notice of takedown by showing either (a) that the Infringed Mark has been cancelled or
          has expired or lapsed or (b) that the registered user has a trademark registration, an unexpired license covering the use, or some other
          relevant right to the Infringed Mark. If the registered user succeeds in showing either (a) or (b) then Photobucket will not remove the
          Infringed Mark. If Photobucket decides to comply with a takedown request, it will do so within a commercially reasonable time after receipt
          of the notice.
        </ContentText>
        <ContentText>
          Notwithstanding the foregoing, Photobucket will comply as appropriate with the terms of any court order relating to alleged trademark
          infringement on the Website.
        </ContentText>

        <Subhead>Rights of Publicity and Use of Name or Likeness</Subhead>
        <ContentText>
          Photobucket respects the rights of individuals to exploit their own name, voice, signature, photograph or likeness and prohibits its users
          from posting Content that would otherwise misappropriate a third party&apos;s right of publicity. If you think that your right of publicity
          is being misappropriated as a result of the content on the Website, please provide our copyright agent (specified above) with the following
          information:
          <blockquote>
            Information reasonably sufficient to permit Photobucket to identify the Content containing your likeness; Your name, address, telephone
            number and, if available, your email address; A statement that you, or in the case of a minor, your parent or legal guardian, (the
            &quot;Authorizing Party&quot;) has not authorized the challenged use, and that the use is not otherwise authorized by law; and A statement
            that the information in the notification is accurate and, under penalty of perjury, that you are the Authorizing Party or that you are
            authorized to act on behalf of the Authorizing Party.
          </blockquote>
        </ContentText>
        <ContentText>
          Upon receipt of notice as described above, Photobucket will take whatever action, in its sole discretion, it deems appropriate, including
          removal of the challenged use from the Website, with notice to the Registered User in question, unless, prior to such removal, the
          Registered User in question sufficiently demonstrates that he or she has appropriate authorization or consent to use your likeness.
        </ContentText>
        <ContentText>
          Notwithstanding the foregoing, Photobucket will comply as appropriate with the terms of any court order relating to the behavior of the
          challenged party identified in such court order.
        </ContentText>

        <Subhead>Questions?</Subhead>
        <ContentText>
          Should you have any questions regarding this policy, please contact us at{' '}
          <ReferenceLink href="mailto:abuse@photobucket.com">abuse@photobucket.com</ReferenceLink>
        </ContentText>
      </ContentWrapper>
    </Box>
  )
}
